import { styled } from "@mui/material/styles";
import { Box, Button, Typography } from "@mui/material";
import colors from "assets/theme/base/colors";

export const DateRangePickerContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    display: "inline-block",
}));

export const TriggerButton = styled(Button)(({ theme }) => ({
    backgroundColor: colors.white.main,
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: "8px",
    padding: "8px 16px",
    color: colors.grey[600],
    textTransform: "none",
    fontWeight: 400,
    fontSize: "14px",
    minWidth: "200px",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    "&:hover": {
        backgroundColor: colors.grey[100],
        border: `1px solid ${colors.primary.main}`,
    },
}));

export const DropdownContainer = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 1300,
    backgroundColor: colors.white.main,
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    minWidth: "580px",
    marginTop: "4px",
}));

export const LayoutContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: "20px",
}));

export const LeftColumn = styled(Box)(({ theme }) => ({
    minWidth: "200px",
    flex: "0 0 200px",
}));

export const RightColumn = styled(Box)(({ theme }) => ({
    flex: "1",
}));

export const PresetButton = styled("button", {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
    marginBottom: "4px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "inherit",
    color: selected ? colors.secondary.main : colors.dark.main,
    textDecoration: selected ? "underline" : "none",
    fontWeight: selected ? 500 : 400,
    "&:hover": {
        backgroundColor: colors.grey[100],
        color: selected ? colors.primary.focus : colors.dark.main,
    },
    "&:focus": {
        outline: `2px solid ${colors.primary.main}`,
        outlineOffset: "2px",
    },
    "&:last-child": {
        marginBottom: 0,
    },
}));

export const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: colors.secondary.main,
    color: colors.white.main,
    textTransform: 'none',
    '&:hover': {
        backgroundColor: colors.secondary.focus,
    },
    '&:disabled': {
        backgroundColor: colors.secondary.main,
        opacity: 0.6,
        color: colors.white.main,
    },
}));

export const CalendarContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
}));

export const CalendarGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    minWidth: "240px",
}));

export const CalendarHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    padding: "0 8px",
}));

export const NavigationButton = styled("button")(({ theme }) => ({
    minWidth: "32px",
    height: "32px",
    padding: "4px",
    border: "none",
    borderRadius: "4px",
    backgroundColor: "transparent",
    color: colors.dark.main,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    "&:hover": {
        backgroundColor: colors.grey[100],
    },
    "&:focus": {
        outline: `2px solid ${colors.primary.main}`,
        outlineOffset: "2px",
    },
    "&:active": {
        backgroundColor: colors.grey[200],
    },
    "& svg": {
        fontSize: "20px",
    },
}));

export const DayHeader = styled(Typography)(({ theme }) => ({
    fontSize: "12px",
    fontWeight: 600,
    color: colors.grey[600],
    textAlign: "center",
    padding: "4px",
}));

export const DayButton = styled(Button, {
    shouldForwardProp: (prop) =>
        prop !== 'isSelected' &&
        prop !== 'isInRange' &&
        prop !== 'isToday' &&
        prop !== 'isDisabled',
})<{
    isSelected?: boolean;
    isInRange?: boolean;
    isToday?: boolean;
    isDisabled?: boolean;
}>(({
    theme,
    isSelected,
    isInRange,
    isToday,
    isDisabled
}) => ({
    minWidth: "32px",
    height: "32px",
    padding: 0,
    fontSize: "14px",
    color: isDisabled
        ? colors.grey[400]
        : isSelected
            ? colors.white.main
            : isToday
                ? colors.primary.main
                : colors.dark.main,
    backgroundColor: isSelected
        ? colors.primary.main
        : isInRange
            ? colors.primary.focus + "20"
            : "transparent",
    border: isToday && !isSelected ? `1px solid ${colors.primary.main}` : "1px solid transparent",
    borderRadius: "4px",
    "&:hover": {
        backgroundColor: isDisabled
            ? "transparent"
            : isSelected
                ? colors.primary.focus
                : colors.grey[100],
    },
    "&:disabled": {
        color: colors.grey[400],
        cursor: "not-allowed",
    },
}));

export const CustomRangeInputs = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "16px",
}));

export const DateInputRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
}));

export const DateInput = styled("input")(({ theme }) => ({
    padding: "8px 12px",
    border: `1px solid ${colors.grey[300]}`,
    borderRadius: "4px",
    fontSize: "14px",
    width: "100%",
    "&::placeholder": {
        fontWeight: 400,
        color: "red",
    },
    "&:focus": {
        outline: "none",
        border: `1px solid ${colors.primary.main}`,
    },
    "&:disabled": {
        backgroundColor: colors.grey[100],
        color: colors.grey[500],
        cursor: "not-allowed",
    },
}));
